import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';
import { onIdentifyUser } from 'actions/UserActions';
import cx from 'classnames';
import _pairs from 'lodash/toPairs';
import dynamic from 'next/dynamic';
import {
  SecondaryNavIdentifier,
  TertiaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import type {
  WCardDesignFamilySearchView,
  WCardDesignFamilySummaryView,
} from '@zola/svc-web-api-ts-client';
import { getSEOData } from 'cards/seo';
import type { SEOData } from 'cards/seo/types';
import Breadcrumbs from 'cards/components/CardListing/Breadcrumbs';
import { Hero } from 'cards/components/CardListing/Hero';
import { CardListingSEOHeader } from 'cards/components/CardListing/CardListingSEOHeader';
import { CardListingHeader } from 'cards/components/CardListing/CardListingRedesign/CardListingHeader';
import CardListingWrapper from 'cards/components/CardListing/CardListingRedesign/CardListingWrapper';
import { getDesktopNavHeight } from 'components/common/header/helpers';
import { getUserContext } from 'selectors/user/userSelectors';
import { getAllMotifs } from 'cards/selectors/cardCatalogSelector';
import MinimalLoader from 'components/common/ui/MinimalLoader';
import NoDesignsAvailable from 'cards/components/CardListing/CardListingRedesign/NoDesignsAvailable/NoDesignsAvailable';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { useResponsiveContext } from 'components/common/ResponsiveContext';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { getFacetedSuites } from 'api/cardCatalog';
import { multipleSearchFilterValues } from 'cards/components/CardListing/CardListingRedesign/CardListingFilter/FilterTypes';
import { SEOContentBlock } from 'cards/components/CardListing/SEOContentBlock';
import { trackProductListViewed, trackProductClicked } from '@zola/tracking-contracts/src/tracking';
import { NAV_DATA } from 'constants/navConstants';
import { trackedFilters, consolidateFiltersByKey } from 'cards/components/CardListing/util';
import { isPostAuthNav3MobileOrDesktop } from 'util/isPostAuthNav3Active';
import { storeInitialColor } from 'cards/util/initialOptions';
import { SuiteTile } from './components/SuiteTile';
import styles from './FullSuitePLP.module.less';
import { SuiteCardsCarousel } from './components/SuiteCardsCarousel';
import {
  getFullSuiteDefaultColor,
  getFullSuiteNrfColor,
  filterMapper,
  mapResponseToFilter,
} from './utils';
import { getNewPathNameAndSearch } from '../CardListing/CardListingRedesign/utils';
import { trackLoadMorePLP, trackProductListFiltered } from '../../../util/trackingHelper';

const DynamicRecentlyViewed = dynamic(() => import('../common/RecentlyViewed'), { ssr: false });
const DynamicCardListingFilter = dynamic<any>(
  () =>
    import('../CardListing/CardListingRedesign/CardListingFilter/CardListingFilter') as Promise<
      any
    >,
  { ssr: false }
);
const DynamicUnifiedNav = dynamic(() => import('components/common/header/UnifiedNav'), {
  loading: () => <div style={{ height: '140px' }} />,
  ssr: false,
});

type NewFilterStateType = {
  offset: number;
  limit: number;
  has_foil: boolean;
  has_custom_photo: boolean;
  has_no_custom_photo: boolean;
  is_letterpress: boolean;
  seo_colors: string[];
  motifs: string[];
  silhouettes: string[];
  sorts: string[];
  highlight_design_family_uuid: string | null;
  check_drafts: boolean;
};

const INITIAL_STATE: NewFilterStateType = {
  // query: '',
  offset: 0,
  limit: 48,
  has_foil: false,
  has_custom_photo: false,
  has_no_custom_photo: false,
  is_letterpress: false,
  seo_colors: [],
  motifs: [],
  silhouettes: [],
  highlight_design_family_uuid: null,
  sorts: [],
  // card_suite_uuids: [],
  check_drafts: true,
};

const updateUrlWithFilters = (newFilter: any) => {
  const { newPathname, newSearch } = getNewPathNameAndSearch(newFilter, 'suites', false);
  const { pathname, search } = browserHistory.getCurrentLocation();

  //* UPDATE THE URL WITH NEW PATHNAME AND QUERY PARAMS
  // Only update the URL if it has changed
  if (newPathname !== pathname || newSearch !== search) {
    // Update the current URL
    browserHistory.push({
      pathname: newPathname,
      search: newSearch,
    });
  }
};

interface FullSuitePLPProps {
  initialSuites: WCardDesignFamilySearchView | null;
  initialFilters: any;
  ssrSeoData: SEOData;
}

export const FullSuitePLP: React.FC<FullSuitePLPProps> = ({
  initialSuites,
  initialFilters,
  ssrSeoData,
}) => {
  const { isMobile, isDesktop } = useResponsiveContext();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [nrfColor, setNrfColor] = useState(initialFilters.seo_colors);
  const [selectedColors, setSelectedColors] = useState<Record<string, string>>({});
  const [filterState, setFilterState] = useState({
    ...INITIAL_STATE,
    ...initialFilters,
  });
  const [suitesList, setSuitesList] = useState(initialSuites);
  const [isBusy, setIsBusy] = useState(false);
  const [scrollActive, setScrollActive] = useState(false);
  const refTopOfPage = useRef() as React.MutableRefObject<HTMLInputElement>;
  const userContext = useSelector(getUserContext);
  const allMotifs = useSelector(getAllMotifs);
  const dispatch = useDispatch();
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [seoData, setSeoData] = useState(ssrSeoData);

  useEffect(() => {
    setSeoData(getSEOData('MATCHING_SUITES', filterState));
  }, [filterState]);

  const trackedSuiteClickedEvent = (suite: WCardDesignFamilySummaryView, index: number) => {
    const defaultColor = getFullSuiteDefaultColor(suite);
    const trackingData = {
      business_category: 'UNATTRIBUTED',
      business_unit: 'PAPER',
      entity_type: 'PAPER',
      card_suite_uuid: suite.familyUuid,
      sku: suite.familyUuid,
      category: 'UNATTRIBUTED',
      name: `${suite.familyDisplayName} Suite`,
      brand: suite.familyDisplayName,
      price: +((suite.suites?.[0]?.imageUrlsByColor?.[defaultColor]?.minPriceCents || 0) / 100),
      quantity: 1,
      image_url: suite.suites?.[0]?.imageUrlsByColor?.[defaultColor]?.plpFrontImageUrl || '',
      position: index || 0,
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:disable-next-line : Sku number | string
    trackProductClicked(trackingData);
  };

  const trackedSuiteViewEvent = () => {
    // Collection of suites
    const products =
      suitesList?.fullSuites?.map((each, i) => {
        const defaultColor = getFullSuiteDefaultColor(each);
        return {
          card_suite_uuid: each.familyUuid || 0,
          sku: each.familyUuid || 0,
          category: 'UNATTRIBUTED',
          name: each.familyKey || '',
          brand: `${each.familyDisplayName} Suite`,
          price: +((each.suites?.[0]?.imageUrlsByColor?.[defaultColor]?.minPriceCents || 0) / 100),
          quantity: 1,
          image_url: each.fullSuiteImageUrlByColor?.[defaultColor]?.fullSuiteImageUrl || '',
          position: i,
        };
      }) || [];

    // New tracking contract
    trackProductListViewed({
      business_category: 'UNATTRIBUTED',
      business_unit: 'PAPER',
      entity_type: 'PAPER',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:disable-next-line
      filters: trackedFilters(consolidateFiltersByKey(filterState)),
      sorts: [],
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore:disable-next-line : TS error because of temp condition to support legacy
      products,
    });
  };

  useEffect(() => {
    dispatch(onIdentifyUser());
  }, [dispatch]);

  useEffect(() => {
    trackedSuiteViewEvent();
  }, [suitesList]);

  useEffect(() => {
    /*
     * By default, on the first page load, the first
     * Suite carousel is opened. We're simulating a Suite tile
     * click event, according to tracking contracts need.
     */
    if (isFirstRender && suitesList?.fullSuites) {
      trackedSuiteClickedEvent(suitesList.fullSuites?.[0] || {}, 0);
    }
  }, [isFirstRender, suitesList]);

  useEffect(() => {
    if (filterState.seo_colors.length) {
      setNrfColor(filterState.seo_colors);
    }
  }, [filterState.seo_colors]);

  useEffect(() => {
    setIsFirstRender(false);
    if (isFirstRender) return;

    if (isBusy) return;
    setIsBusy(true);
    updateUrlWithFilters(filterState);
    /*
     * `featured_artist` is not a valid BE field and will be ignored. It's mapped as `third_party`.
     * `featured_artist` is only used on the FE for label/url purposes
     */
    const requestBody = {
      ...filterState,
      ...(filterState.featured_artist ? { third_party: true } : {}),
    };
    getFacetedSuites(filterMapper(requestBody))
      .then(res => {
        if (!res) return;

        const newValues = mapResponseToFilter(res);
        setSuitesList(
          filterState.offset === 0
            ? newValues
            : {
                ...newValues,
                fullSuites: [...(suitesList?.fullSuites || []), ...(newValues.fullSuites || [])],
              }
        );
      })
      .finally(() => setIsBusy(false));
  }, [filterState]);

  if (!suitesList) return null;

  const { fullSuites = [], selectedThemeKey, displayableTotal = 0 } = suitesList;

  const insertOffset = isMobile ? 2 : 4;
  let insertSuiteIndex =
    selectedIndex >= 0 ? Math.ceil((selectedIndex + 1) / insertOffset) * insertOffset - 1 : -1;

  if (insertSuiteIndex >= fullSuites.length) {
    insertSuiteIndex = fullSuites.length - 1;
  }

  function scrollToTop() {
    if (refTopOfPage && refTopOfPage.current && scrollActive) {
      // Offset to account for navigation height
      const heightWithSecondary = getDesktopNavHeight(false, userContext.is_guest);
      const topNavOffset = isMobile ? 58 : heightWithSecondary;
      window.scrollTo({
        top: refTopOfPage.current.offsetTop - topNavOffset,
        left: 0,
      });
    }
  }

  function onUpdate(newFilterState: NewFilterStateType) {
    if (isBusy) {
      return;
    }
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'never[]' is not assignable to pa...
    setSuitesList([]);
    setFilterState(newFilterState);
    trackProductListFiltered({
      list_id: 'suites',
      category: BUSINESS_UNIT.INVITATIONS,
      filters: _pairs(newFilterState),
      sorts: newFilterState.sorts,
    });
    scrollToTop();
    setSelectedIndex(-1);
  }

  const onFilterUpdate = (key: string, value: any) => {
    if (isBusy) return;

    const newFilterState = {
      ...filterState,
      offset: 0,
      [key]: value,
    };
    onUpdate(newFilterState);
  };

  function clearFilters() {
    onUpdate(INITIAL_STATE);
    browserHistory.push(`/wedding-planning/suites/shop`);
  }

  function handleMultipleFiltersUpdate(newValues: multipleSearchFilterValues) {
    const newFilterState = {
      ...filterState,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'featured_artist' does not exist on type ...
      featured_artist: newValues.featured_artist || filterState.featured_artist || false,
      has_foil: newValues.has_foil || filterState.has_foil || false,
      has_custom_photo: newValues.has_custom_photo || filterState.has_custom_photo || false,
      has_no_custom_photo:
        newValues.has_no_custom_photo || filterState.has_no_custom_photo || false,
      is_letterpress: newValues.is_letterpress || filterState.is_letterpress || false,
      seo_colors: newValues.seo_colors || filterState.seo_colors || [],
      motifs: newValues.motifs || filterState.motifs || [],
      silhouettes: newValues.silhouettes || filterState.silhouettes || [],
    };
    onUpdate(newFilterState);
  }

  const loadMore = () => {
    const newFilterState = {
      ...filterState,
      offset: filterState.offset + filterState.limit,
    };
    setFilterState(newFilterState);
    trackLoadMorePLP();
  };

  const isPostAuthNav3 = isPostAuthNav3MobileOrDesktop(userContext);

  return (
    <React.Fragment>
      <CardListingSEOHeader seoData={seoData} />
      <div className="row">
        <DynamicUnifiedNav
          enableSticky={!isMobile}
          secondaryType="invites"
          activePrimaryLink={NAV_DATA.PRIMARY.PAPER}
          activeSecondaryLink={
            isPostAuthNav3
              ? SecondaryNavIdentifier.PAPER_SHOP
              : SecondaryNavIdentifier.MATCHING_SUITES
          }
          activeTertiaryLink={TertiaryNavIdentifier.COLLECTIONS_PAPER}
          emitDirectionEvents
        />
      </div>
      <CardListingWrapper
        activeCardEvent="MATCHING_SUITES"
        isMobile={isMobile}
        contentClassName={styles.cardListingWrapper}
      >
        <CardListingHeader className={styles.cardListingHeader} ref={refTopOfPage}>
          <Breadcrumbs currentCardType="MATCHING_SUITES" />
          <Hero activeCardEvent="MATCHING_SUITES" seoData={seoData} />
          <div className={styles.cardListingFilterContainer}>
            <DynamicCardListingFilter
              onUpdateSearchFilters={onFilterUpdate}
              onUpdateMultipleSearchFilters={handleMultipleFiltersUpdate}
              searchFilters={filterState}
              handleFilterUpdate={onFilterUpdate}
              scrollActive={scrollActive}
              setScrollActive={setScrollActive}
              activeCardEvent="MATCHING_SUITES"
              allowMultiSample={false}
              clearFilters={clearFilters}
              scrollToTop={scrollToTop}
              isMobile={isMobile}
              isDesktop={isDesktop}
              cardCatalogBusy={isBusy}
              allMotifs={allMotifs}
              userContext={userContext}
              multiSampleState={{}}
              searchTotal={displayableTotal}
            />
          </div>
        </CardListingHeader>
        <div className={styles.loaderAnchor}>
          {isBusy && !fullSuites.length && (
            <div className={styles.loaderContainer}>
              <MinimalLoader />
            </div>
          )}
          {!isBusy && fullSuites.length === 0 && <NoDesignsAvailable clearAll={clearFilters} />}
          {fullSuites.length > 0 && (
            <div className={styles.suites}>
              {fullSuites.map((suite, index) => (
                <React.Fragment key={suite.familyUuid}>
                  <SuiteTile
                    suite={suite}
                    onClick={() => {
                      trackedSuiteClickedEvent(suite, index);
                      setSelectedIndex(index === selectedIndex ? -1 : index);
                    }}
                    onColorChange={(color: string) => {
                      setSelectedColors({ ...selectedColors, [suite.familyUuid || '']: color });
                      const suiteUUIDs =
                        suite.suites?.reduce((acc, s) => {
                          if (s.uuid) acc.push(s.uuid);
                          return acc;
                        }, [] as string[]) || [];
                      storeInitialColor(suiteUUIDs, color);
                    }}
                    active={selectedIndex === index}
                    nrfColor={getFullSuiteNrfColor(nrfColor, suite)}
                  />
                  {index === insertSuiteIndex && (
                    <SuiteCardsCarousel
                      key={selectedIndex}
                      isFirstRender={isFirstRender}
                      selectedSuiteIndex={selectedIndex}
                      selectedThemeKey={selectedThemeKey}
                      activeColor={
                        (fullSuites[selectedIndex].familyUuid &&
                          selectedColors[fullSuites[selectedIndex].familyUuid || '']) ||
                        getFullSuiteNrfColor(nrfColor, fullSuites[selectedIndex]) ||
                        getFullSuiteDefaultColor(fullSuites[selectedIndex])
                      }
                      suites={fullSuites}
                      hasWeddingAccount={userContext && userContext.has_wedding_account}
                      isMobileView={isMobile}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
        {filterState.limit + filterState.offset <= displayableTotal && fullSuites.length !== 0 && (
          <div className={styles.divider} />
        )}
        <div className={styles.suitesActions}>
          {filterState.limit + filterState.offset <= displayableTotal && fullSuites.length !== 0 ? (
            <React.Fragment>
              <ButtonV3
                variant="primary"
                onClick={loadMore}
                className={styles.lowercase}
                disabled={isBusy}
              >
                {isBusy
                  ? 'Loading...'
                  : `Show ${filterState.limit + filterState.offset} of ${displayableTotal}`}
              </ButtonV3>
            </React.Fragment>
          ) : null}
        </div>
      </CardListingWrapper>
      <DynamicRecentlyViewed className={cx('row', { [styles.blueBg]: isMobile })} />
      <div className="row">
        <SEOContentBlock
          seoData={seoData}
          activeCardEvent="MATCHING_SUITES"
          allMotifs={allMotifs}
        />
      </div>
    </React.Fragment>
  );
};
